/*
 *  TTTech nerve-management-system-frontend
 *  Copyright(c) 2021. TTTech Industrial Automation AG.
 *
 *  ALL RIGHTS RESERVED.
 *
 *  Usage of this software, including source code, netlists, documentation,
 *  is subject to restrictions and conditions of the applicable license
 *  agreement with TTTech Industrial Automation AG or its affiliates.
 *
 *  All trademarks used are the property of their respective owners.
 *
 *  TTTech Industrial Automation AG and its affiliates do not assume any liability
 *  arising out of the application or use of any product described or shown
 *  herein. TTTech Industrial Automation AG and its affiliates reserve the right to
 *  make changes, at any time, in order to improve reliability, function or
 *  design.
 *
 *  Contact Information:
 *  support@tttech-industrial.com
 *
 *  TTTech Industrial Automation AG, Schoenbrunnerstrasse 7, 1040 Vienna, Austria
 *
 */
import {
  handleDockerRegistryEvents,
  handleNodeLifecycleEvents,
  handleNodePersistentDataEvent,
  handleDnaStatusEvent,
  handleWorkloadEvents,
  handleNodeInformationUpdateEvent,
  handleSelectedNodeDeployedWlUpdatedEvent,
  handleBomCampaignCloudEvents,
  handleAuthAndCrmUserEvents,
  handleRemoteConnectionEvents,
  handleBackupLogEvent,
  handleSnapshotEvent,
} from './message-handler-service';
/**
 * List topics that authenticated user should subscribe to
 *
 * @param userId - Authenticated user id
 * @param sessionToken - second part of sessionId (sessionId.split("-")[1])
 * @returns {[{topic: string}, {topic: string}]}
 */
const listAuthUserTopics = () => [
  // topic used to receive info if an error has been occurred when try to start/stop
  // deployed workload,or when Docker image is being downloaded
  {
    matchBy: 'service_workload/evt',
    topic: 'oblo/+/session/+/svc/service_workload/evt',
    action: handleWorkloadEvents,
  },
];
/**
 * List topics that all users should be subscribed to
 */
const listAlwaysSubscribedTopics = () => [
  {
    matchBy: 'auth',
    topic: 'oblo/all/admin/svc/auth/evt',
    action: handleAuthAndCrmUserEvents,
  },

  {
    matchBy: 'crm',
    topic: 'oblo/all/admin/svc/crm/evt',
    action: handleAuthAndCrmUserEvents,
  },
];
/**
 * List Node topics that client will subscribe to
 *
 * @param userId - current (selected) node user id
 * @param serialNumber - current (selected) serialNumber
 * @param sessionId - authenticated user sessionId
 * @returns {[{topic: string}, {topic: string}, {topic: string}]}
 */
const listNodeTopics = ({ userId = '+', serialNumber = '+' } = {}) => [
  // ohm events from the specific Node
  {
    matchBy: 'ohm/evt',
    topic: `oblo/${userId}/gtw/${serialNumber}/ohm/evt`,
    action: handleSelectedNodeDeployedWlUpdatedEvent,
  },
  // NodePersistentDataEvent should be listened to update 'ovdmStarted' property in a tree model
  {
    matchBy: 'ovdm/evt',
    topic: `oblo/+/gtw/${serialNumber}/ovdm/evt`,
    action: handleNodePersistentDataEvent,
  },
];

/** Snapshot topic
 *
 * @param serialNumber - current (selected) serialNumber
 * @returns {[{topic: string}]}
 */
const snapshotTopic = ({ serialNumber = '+' } = {}) => [
  // snapshot events from the specific node
  {
    matchBy: 'snapshot',
    topic: `nerve/node/${serialNumber}/snapshot`,
    action: handleSnapshotEvent,
  },
];

/** Backups topic
 *
 * @param serialNumber - current (selected) serialNumber
 * @returns {[{topic: string}]}
 */
const backupsTopic = ({ serialNumber = '+' } = {}) => [
  // backup events from the specific node
  {
    matchBy: 'workload_backup',
    topic: `nerve/node/${serialNumber}/workload_backup`,
    action: handleBackupLogEvent,
  },
];

/**
 * List Cloud topics that client will subscribe to
 *
 * @returns {[{topic: string}, {topic: string}, {topic: string}]}
 */
const listCloudTopics = () => [
  // info about campaign/task updates
  { matchBy: 'bom/evt', topic: 'oblo/all/admin/svc/bom/evt', action: handleBomCampaignCloudEvents },
];

/**
 * Initiate remote connection
 *
 * @returns {[{topic: string}, {topic: string}, {topic: string}]}
 */
const initRemoteConnectionTopics = () => [
  {
    matchBy: 'remote_connection',
    topic: 'oblo/svc/remote_connection/+/evt',
    action: handleRemoteConnectionEvents,
  },
];

/** DNA topic
 *
 * @param serialNumber - current (selected) serialNumber
 * @returns {[{topic: string}]}
 */
const dnaTopic = ({ serialNumber = '+' } = {}) => [
  // dna events from the specific node
  // TODO matchBy should be unique param from topic field. Because of that topic should be changed
  // F.e. topic: `nerve/node/${serialNumber}/dna` or `nerve/node/${serialNumber}/dna/evt'
  {
    name: 'dna_status_evt',
    matchBy: 'evt',
    topic: `nerve/node/${serialNumber}/evt`,
    action: handleDnaStatusEvent,
  },
];

const nodeLifecycleTopic = () => [
  // node lifecycle events from specific node
  {
    matchBy: 'lifecycle',
    topic: `nerve/node/lifecycle`,
    action: handleNodeLifecycleEvents,
  },
];

const nodeUpdateTopic = () => [
  // node information update events from specific node
  {
    matchBy: 'update',
    topic: `nerve/node/update`,
    action: handleNodeInformationUpdateEvent,
  },
];

const dockerRegistryTopics = () => [
  {
    matchBy: 'docker_registry',
    topic: `nerve/workloads/docker_registry/image_events`,
    action: handleDockerRegistryEvents,
  },
];

export default {
  alwaysSubscribed: listAlwaysSubscribedTopics,
  node: listNodeTopics,
  nodeLifecycle: nodeLifecycleTopic,
  nodeUpdate: nodeUpdateTopic,
  cloud: listCloudTopics,
  authUser: listAuthUserTopics,
  snapshot: snapshotTopic,
  initRc: initRemoteConnectionTopics,
  backups: backupsTopic,
  dna: dnaTopic,
  dockerRegistry: dockerRegistryTopics,
};
